import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  ICommentFieldsFragment,
  IConversationThreadFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import { theme } from '../../../../../styles/theme';
import Avatar from '../../../../Avatar';
import ReadOnlyConversationMessage from '../../../ReadOnlyConversationMessage';
import CommentImageEmbed from '../../../CommentImageEmbed';
import { SingleSelectOption } from '../../../../Dropdown/SingleSelect';
import ActionsMenu from '../../../../ActionsMenu';
import useUser from '../../../../../utils/hooks/useUser';
import EntityConversationThreadCommentInput from './EntityConversationThreadCommentInput';
import CommentReactions from './CommentReactions';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../../../ActionsVisibilityContext';
import { EmojiClickData } from 'emoji-picker-react';
import useChangeCommentReaction from '../../../hooks/useChangeCommentReaction';
import AddCommentReaction from './AddCommentReaction';

interface IProps {
  thread: IConversationThreadFieldsFragment;
  comment: ICommentFieldsFragment;
  onDelete: () => void;
  onEdit: () => void;
  onCancelUpdate: () => void;
  isLast: boolean;
  isFirst: boolean;
  isEditing?: boolean;
}

const Container = styled.div`
  display: flex;
  column-gap: 4px;
  padding: 8px 4px 8px 12px;
`;
const ContentWrapper = styled.div`
  flex: 1;
`;
const HorizontalRow = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 4px;
`;
const CreatorName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const CreatedAtValue = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.secondary800};
`;
const StyledActionsMenu = styled(ActionsMenu)`
  width: 24px;
  margin-left: auto;
  text-align: center;
`;
const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 24px;
  min-height: 24px;
  column-gap: 2px;
  margin-top: 2px;
`;

const EntityConversationThreadComment = ({
  thread,
  comment,
  onDelete,
  onEdit,
  onCancelUpdate,
  isFirst,
  isLast,
  isEditing,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const user = useUser();
  const pickerOpened = useRef(false);
  const { actionsVisible, showActions, hideActions } =
    useActionsVisibilityContext();
  const { handleAddReaction, handleRemoveReaction } = useChangeCommentReaction({
    threadId: thread.id,
    commentId: comment.id,
    entity: {
      id: thread.entity.id,
      type: thread.entity.type,
    },
  });

  const { attachedImageEmbed, creator, createdAt, message, reactions } =
    comment;
  const htmlMessage = message?.html;
  const imageUrl = attachedImageEmbed?.serveUrl;
  const isCommentByUser = user.id === creator.id;

  // In case if it's the last comment, scroll it into view
  useEffect(() => {
    if (isLast && ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isLast]);

  const handleMouseEnter = () => {
    // Disable action visibility context feature when comment has reactions
    if (!reactions.length) {
      showActions();
    }
  };
  const handleMouseLeave = () => {
    // Disable action visibility context feature when comment has reactions
    if (!reactions.length) {
      if (pickerOpened.current) {
        return;
      }

      hideActions();
    }
  };

  const handleOpenEmojis = () => {
    pickerOpened.current = true;
  };
  const handleCloseEmojis = () => {
    pickerOpened.current = false;
    if (actionsVisible) {
      hideActions();
    }
  };
  const handleAddFirstReaction = (emojiData: EmojiClickData) => {
    handleAddReaction(emojiData.unified);
  };

  const showDeleteOption = !isFirst;

  return (
    <Container
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Avatar picture={creator.picture} size={16} />
      {isEditing ? (
        <EntityConversationThreadCommentInput
          thread={thread}
          comment={comment}
          onCancel={onCancelUpdate}
        />
      ) : (
        <ContentWrapper>
          <HorizontalRow>
            <CreatorName>{creator.name}</CreatorName>
            <CreatedAtValue>{moment.utc(createdAt).fromNow()}</CreatedAtValue>
            {isCommentByUser && (
              <StyledActionsMenu
                strategy="fixed"
                iconWrapperSize={16}
                iconSize={16}
              >
                <SingleSelectOption onClick={onEdit} label={'Edit comment'} />
                {showDeleteOption && (
                  <SingleSelectOption
                    onClick={onDelete}
                    label={'Delete comment'}
                  />
                )}
              </StyledActionsMenu>
            )}
          </HorizontalRow>
          <Content>
            <div>
              {htmlMessage && (
                <ReadOnlyConversationMessage content={htmlMessage} />
              )}
              {imageUrl && <CommentImageEmbed imageUrl={imageUrl} />}
            </div>
            {actionsVisible && !reactions.length && (
              <AddCommentReaction
                onSelect={handleAddFirstReaction}
                onOpen={handleOpenEmojis}
                onClose={handleCloseEmojis}
              />
            )}
          </Content>

          {reactions.length > 0 && (
            <CommentReactions
              reactions={reactions}
              onAdd={handleAddReaction}
              onRemove={handleRemoveReaction}
            />
          )}
        </ContentWrapper>
      )}
    </Container>
  );
};

export default withActionsVisibilityContext(EntityConversationThreadComment);
